"use client";

import React, { useCallback, useEffect, useState } from "react";
import { ArrowLeft, Loader2, Play, Plus, Save, X } from "lucide-react";
import { Button } from "../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuTrigger } from "../../ui/dropdown-menu";
import { getFY_YEAR } from "@/utils/sharepoint-env";
import { formatLeaseDialogCell } from "@/utils/lease-dialog-format";
import {
  computeLeaseAccountingExceptionDisclosure,
  computeLeaseDisclosure,
  computeLeaseMaturityAnalysis,
  type LeaseAccountingExceptionDisclosureComputed,
  type LeaseDisclosureComputed,
  type LeaseMaturityAnalysisComputed,
} from "@/utils/leaseDisclosureCompute";

interface LeaseDisclosureIndASProps {
  setActiveSection?: (section: string) => void;
}

const ASSESSMENT_OUT = "Execution_LEASE_AssessmentOut.json";
const MOD_OUT = "Execution_LEASE_Modification.json";
const REASS_OUT = "Execution_LEASE_Reassessment.json";
const RECON_FILE = "Execution_LEASE_DisclosureRecon.json";
const ENGAGEMENT_DETAILS_FILE = "EngagementDetails.json";
const ACC_EXP_OUT = "Execution_LEASE_AccExpOut.json";
const ACC_EXP_INP = "Execution_LEASE_AccExpInp.json";

function fmt(n: number) {
  return formatLeaseDialogCell(n, "Amount");
}
function fmtPrev(n: number, hasPrevYearData: boolean) {
  return hasPrevYearData ? fmt(n) : "-";
}

function asNumber(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function ddMmYyyy(d: Date): string {
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
}

function getCy(obj: any, section: string, row: string): number {
  return asNumber(obj?.[section]?.[row]?.["Current Year"]);
}

function getPyFromPrev(objPrev: any, section: string, row: string): number {
  return asNumber(objPrev?.[section]?.[row]?.["Current Year"]);
}

type AccExpLedgerOption = {
  ledger_name: string;
  latest_date: string;
  amount: number;
};

function parseDdMmYyToTime(s: string): number {
  const m = String(s || "").trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{2})$/);
  if (!m) return 0;
  const dd = parseInt(m[1], 10);
  const mm = parseInt(m[2], 10) - 1;
  const yy = 2000 + parseInt(m[3], 10);
  const d = new Date(yy, mm, dd);
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
}

function buildFs20036LedgerOptions(mapping: unknown): AccExpLedgerOption[] {
  if (!mapping || typeof mapping !== "object") return [];
  const byName = new Map<string, AccExpLedgerOption>();
  for (const ledgers of Object.values(mapping as Record<string, unknown>)) {
    if (!Array.isArray(ledgers)) continue;
    for (const item of ledgers as Record<string, unknown>[]) {
      const sid = Number(
        item.fs_sub_line_id ?? item.fsSubLineId ?? item.note_line_id ?? item.noteLineId ?? NaN,
      );
      if (sid !== 20036) continue;
      const amountDict = item.amount;
      if (!amountDict || typeof amountDict !== "object") continue;
      const keys = Object.keys(amountDict as Record<string, unknown>);
      if (!keys.length) continue;
      let latest = keys[0];
      let latestT = parseDdMmYyToTime(latest);
      for (const k of keys.slice(1)) {
        const t = parseDdMmYyToTime(k);
        if (t > latestT) {
          latest = k;
          latestT = t;
        }
      }
      const ledgerName = String(item.ledger_name ?? "")
        .replace(/_x000D/g, "")
        .replace(/\n/g, "")
        .replace(/_+$/g, "")
        .trim();
      if (!ledgerName) continue;
      const amt = asNumber((amountDict as Record<string, unknown>)[latest]);
      byName.set(ledgerName, { ledger_name: ledgerName, latest_date: latest, amount: amt });
    }
  }
  return [...byName.values()].sort((a, b) => a.ledger_name.localeCompare(b.ledger_name));
}

function parseReportingDate(s: string | null | undefined): Date | null {
  if (!s || !String(s).trim()) return null;
  const t = String(s).trim();
  if (t.includes("/")) {
    const parts = t.split("/");
    if (parts.length === 3) {
      const a = parseInt(parts[0], 10);
      const b = parseInt(parts[1], 10);
      let y = parseInt(parts[2], 10);
      if (!Number.isFinite(a) || !Number.isFinite(b) || !Number.isFinite(y)) return null;
      if (y < 100) y = 2000 + y;

      // Heuristic:
      // - if first part > 12 => dd/mm/yyyy
      // - if second part > 12 => mm/dd/yyyy
      // - if ambiguous (both <= 12), try mm/dd first then dd/mm
      const tryMmDd = (): Date | null => {
        const d = new Date(y, a - 1, b);
        if (d.getFullYear() !== y) return null;
        if (d.getMonth() !== a - 1) return null;
        if (d.getDate() !== b) return null;
        return d;
      };
      const tryDdMm = (): Date | null => {
        const d = new Date(y, b - 1, a);
        if (d.getFullYear() !== y) return null;
        if (d.getMonth() !== b - 1) return null;
        if (d.getDate() !== a) return null;
        return d;
      };

      if (a > 12 && b <= 12) return tryDdMm();
      if (b > 12 && a <= 12) return tryMmDd();
      return tryMmDd() || tryDdMm();
    }
  }

  // Common SharePoint engagement format: dd-mm-yyyy (e.g. 31-03-2025)
  const dash = t.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  if (dash) {
    const d = parseInt(dash[1], 10);
    const m = parseInt(dash[2], 10);
    const y = parseInt(dash[3], 10);
    if (Number.isFinite(d) && Number.isFinite(m) && Number.isFinite(y)) {
      const dt = new Date(y, m - 1, d);
      if (!Number.isNaN(dt.getTime())) return dt;
    }
  }

  const iso = new Date(t);
  if (!Number.isNaN(iso.getTime())) return iso;
  return null;
}

export default function LeaseDisclosureIndAS({ setActiveSection }: LeaseDisclosureIndASProps) {
  const [loading, setLoading] = useState(false);
  const [repLabel, setRepLabel] = useState<string>("");
  const [repDateKey, setRepDateKey] = useState<string>("");
  const [computed, setComputed] = useState<LeaseDisclosureComputed | null>(null);
  const [maturity, setMaturity] = useState<LeaseMaturityAnalysisComputed | null>(null);
  const [accExpDisclosure, setAccExpDisclosure] = useState<LeaseAccountingExceptionDisclosureComputed | null>(null);
  const [accExpReconLedgerOptions, setAccExpReconLedgerOptions] = useState<AccExpLedgerOption[]>([]);
  const [accExpReconSelectedLedgers, setAccExpReconSelectedLedgers] = useState<string[]>([]);
  const [accExpReconSelectedTotalPrev, setAccExpReconSelectedTotalPrev] = useState(0);
  const [hasPrevYearData, setHasPrevYearData] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [userReconciliations, setUserReconciliations] = useState<Array<{ reason: string; amount: number }>>([]);
  const [accExpUserReconciliations, setAccExpUserReconciliations] = useState<Array<{ reason: string; amount: number }>>([]);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");

  const loadRecon = useCallback(async () => {
    try {
      // Resolve reporting date first so we can pick current and prior date blocks.
      let rep: Date | null = null;
      try {
        const repRes = await (window as any).sharePointAPI?.getReportingDate?.();
        const repDateRaw =
          repRes?.rep_date ?? repRes?.repDate ?? repRes?.rep_date_str ?? repRes?.repDateStr ?? null;
        if (repDateRaw) rep = parseReportingDate(String(repDateRaw));
      } catch {
        // ignore
      }
      if (!rep) return;

      const currKey = ddMmYyyy(rep);
      const prevKey = ddMmYyyy(new Date(rep.getFullYear() - 1, rep.getMonth(), rep.getDate()));

      const res = await (window as any).sharePointAPI?.downloadClientJson?.(RECON_FILE);
      const tbRes = await (window as any).sharePointAPI?.downloadTrialBalanceMapping?.({ includeAmounts: true });
      setAccExpReconLedgerOptions(buildFs20036LedgerOptions(tbRes?.success ? tbRes.data : null));
      const root = res?.success && res?.data && typeof res.data === "object" ? (res.data as Record<string, any>) : null;
      if (!root) return;

      const curr = root[currKey];
      if (!curr || typeof curr !== "object") return;
      const prev = root[prevKey] && typeof root[prevKey] === "object" ? root[prevKey] : null;
      setHasPrevYearData(Boolean(prev));

      setRepDateKey(currKey);
      setRepLabel(rep.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }));

      const c: LeaseDisclosureComputed = {
        reportingDateLabel: String(curr.reportingDateLabel ?? currKey),
        cyOpeningTb: getCy(curr, "Lease Liabilities", "Opening Balance"),
        pyOpeningTb: prev ? getPyFromPrev(prev, "Lease Liabilities", "Opening Balance") : 0,
        additionsCy: getCy(curr, "Lease Liabilities", "Additions"),
        additionsPy: prev ? getPyFromPrev(prev, "Lease Liabilities", "Additions") : 0,
        adjustmentsCy: getCy(curr, "Lease Liabilities", "Adjustment of Modifications/Reassessment"),
        adjustmentsPy: prev ? getPyFromPrev(prev, "Lease Liabilities", "Adjustment of Modifications/Reassessment") : 0,
        accretionCy: getCy(curr, "Lease Liabilities", "Accretion of Interest"),
        accretionPy: prev ? getPyFromPrev(prev, "Lease Liabilities", "Accretion of Interest") : 0,
        paymentsCy: Math.abs(getCy(curr, "Lease Liabilities", "Payments")),
        paymentsPy: prev ? Math.abs(getPyFromPrev(prev, "Lease Liabilities", "Payments")) : 0,
        closingSumCy: getCy(curr, "Lease Liabilities", "Closing Balance"),
        closingSumPy: prev ? getPyFromPrev(prev, "Lease Liabilities", "Closing Balance") : 0,
        closingPerScheduleCy: getCy(curr, "Lease Liabilities", "Closing Balance as per all computations"),
        closingPerSchedulePy: prev ? getPyFromPrev(prev, "Lease Liabilities", "Closing Balance as per all computations") : 0,
        tbAtRepCy: getCy(curr, "Lease Liabilities", "Closing Balance as per TB"),
        tbAtRepPy: prev ? getPyFromPrev(prev, "Lease Liabilities", "Closing Balance as per TB") : 0,
        paymentTraceLines: [],
      };
      setComputed(c);

      const m: LeaseMaturityAnalysisComputed = {
        currentYear: {
          year1: getCy(curr, "Maturity Analysis", "Year 1"),
          year2: getCy(curr, "Maturity Analysis", "Year 2"),
          year3: getCy(curr, "Maturity Analysis", "Year 3"),
          year4: getCy(curr, "Maturity Analysis", "Year 4"),
          year5: getCy(curr, "Maturity Analysis", "Year 5"),
          year6Onwards: getCy(curr, "Maturity Analysis", "Year 6 onwards"),
          unmaturedFinanceCharges: getCy(curr, "Maturity Analysis", "Less: unmatured finance charges"),
          closingBalance: getCy(curr, "Maturity Analysis", "Closing Balance"),
          nonCurrent: getCy(curr, "Maturity Analysis", "Non-current"),
          current: getCy(curr, "Maturity Analysis", "Current"),
        },
        previousYear: {
          year1: prev ? getPyFromPrev(prev, "Maturity Analysis", "Year 1") : 0,
          year2: prev ? getPyFromPrev(prev, "Maturity Analysis", "Year 2") : 0,
          year3: prev ? getPyFromPrev(prev, "Maturity Analysis", "Year 3") : 0,
          year4: prev ? getPyFromPrev(prev, "Maturity Analysis", "Year 4") : 0,
          year5: prev ? getPyFromPrev(prev, "Maturity Analysis", "Year 5") : 0,
          year6Onwards: prev ? getPyFromPrev(prev, "Maturity Analysis", "Year 6 onwards") : 0,
          unmaturedFinanceCharges: prev ? getPyFromPrev(prev, "Maturity Analysis", "Less: unmatured finance charges") : 0,
          closingBalance: prev ? getPyFromPrev(prev, "Maturity Analysis", "Closing Balance") : 0,
          nonCurrent: prev ? getPyFromPrev(prev, "Maturity Analysis", "Non-current") : 0,
          current: prev ? getPyFromPrev(prev, "Maturity Analysis", "Current") : 0,
        },
      };
      setMaturity(m);

      const ae: LeaseAccountingExceptionDisclosureComputed = {
        currentYear: {
          shortTermExpense: getCy(
            curr,
            "Lease Accounting Exception",
            "Expense relating to short-term leases accounted for applying paragraph 6",
          ),
          lowValueExpense: getCy(
            curr,
            "Lease Accounting Exception",
            "Expense relating to lease of low-value assets accounted for applying paragraph 6",
          ),
        },
        previousYear: {
          shortTermExpense: prev
            ? getPyFromPrev(
                prev,
                "Lease Accounting Exception",
                "Expense relating to short-term leases accounted for applying paragraph 6",
              )
            : 0,
          lowValueExpense: prev
            ? getPyFromPrev(
                prev,
                "Lease Accounting Exception",
                "Expense relating to lease of low-value assets accounted for applying paragraph 6",
              )
            : 0,
        },
      };
      setAccExpDisclosure(ae);
      const accExpReconCurr = curr?.["Lease Accounting Exception"]?.["User reconciliation"];
      const selectedCurr = Array.isArray(accExpReconCurr?.selected_ledgers)
        ? accExpReconCurr.selected_ledgers.map((x: any) => String(x))
        : [];
      setAccExpReconSelectedLedgers(selectedCurr);
      const accExpReconPrev = prev?.["Lease Accounting Exception"]?.["User reconciliation"];
      setAccExpReconSelectedTotalPrev(asNumber(accExpReconPrev?.selected_total));

      const accExpUrRows = accExpReconCurr?.rows;
      if (Array.isArray(accExpUrRows)) {
        setAccExpUserReconciliations(
          accExpUrRows.map((r: any) => ({
            reason: String(r?.reason || ""),
            amount: Number(r?.amount) || 0,
          })),
        );
      } else {
        setAccExpUserReconciliations([]);
      }

      const rows = curr?.["Lease Liabilities"]?.["User reconciliation"]?.rows;
      if (Array.isArray(rows)) {
        setUserReconciliations(
          rows.map((r: any) => ({
            reason: String(r?.reason || ""),
            amount: Number(r?.amount) || 0,
          })),
        );
      }
    } catch {
      /* optional file */
    }
  }, []);

  const runCompute = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      let rep: Date | null = null;
      // Read rep_date directly from EngagementDetails.json (no get_frf.py execution).
      try {
        const repRes = await (window as any).sharePointAPI?.getReportingDate?.();
        const repDateRaw =
          repRes?.rep_date ?? repRes?.repDate ?? repRes?.rep_date_str ?? repRes?.repDateStr ?? null;
        if (repDateRaw) rep = parseReportingDate(String(repDateRaw));
      } catch {
        // ignore and fallback
      }
      if (!rep) {
        setLoadError("Reporting date not found. Ensure EngagementDetails.json in SharePoint contains `rep_date` (e.g. `31-03-2025`).");
        setComputed(null);
        return;
      }
      setRepLabel(rep.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }));
      const dd = String(rep.getDate()).padStart(2, "0");
      const mm = String(rep.getMonth() + 1).padStart(2, "0");
      const yyyy = rep.getFullYear();
      setRepDateKey(`${dd}-${mm}-${yyyy}`);

      const [assessment, mod, reass, accExpRes, accExpInpRes, tbRes, reconRes] = await Promise.all([
        (window as any).sharePointAPI?.downloadClientJson?.(ASSESSMENT_OUT),
        (window as any).sharePointAPI?.downloadClientJson?.(MOD_OUT),
        (window as any).sharePointAPI?.downloadClientJson?.(REASS_OUT),
        (window as any).sharePointAPI?.downloadClientJson?.(ACC_EXP_OUT),
        (window as any).sharePointAPI?.downloadClientJson?.(ACC_EXP_INP),
        (window as any).sharePointAPI?.downloadTrialBalanceMapping?.({ includeAmounts: true }),
        (window as any).sharePointAPI?.downloadClientJson?.(RECON_FILE),
      ]);

      const aData = assessment?.success ? assessment.data : null;
      const mData = mod?.success ? mod.data : null;
      const rData = reass?.success ? reass.data : null;
      const accExpData = accExpRes?.success ? accExpRes.data : null;
      const accExpInpData = accExpInpRes?.success ? accExpInpRes.data : null;
      const tbData = tbRes?.success ? tbRes.data : null;
      setAccExpReconLedgerOptions(buildFs20036LedgerOptions(tbData));
      const reconRoot =
        reconRes?.success && reconRes?.data && typeof reconRes.data === "object"
          ? (reconRes.data as Record<string, any>)
          : null;

      const prevKey = ddMmYyyy(new Date(rep.getFullYear() - 1, rep.getMonth(), rep.getDate()));
      const currKey = ddMmYyyy(rep);
      const prevBlock = reconRoot?.[prevKey] && typeof reconRoot[prevKey] === "object" ? reconRoot[prevKey] : null;
      const currBlock = reconRoot?.[currKey] && typeof reconRoot[currKey] === "object" ? reconRoot[currKey] : null;
      setHasPrevYearData(Boolean(prevBlock));

      if (!aData?.leases?.length) {
        setLoadError("Execution_LEASE_AssessmentOut.json is missing or empty. Run Lease Testing.");
        setComputed(null);
        return;
      }

      const result = computeLeaseDisclosure(rep, aData, mData, rData, tbData);
      setComputed({
        ...result,
        pyOpeningTb: prevBlock ? getPyFromPrev(prevBlock, "Lease Liabilities", "Opening Balance") : 0,
        additionsPy: prevBlock ? getPyFromPrev(prevBlock, "Lease Liabilities", "Additions") : 0,
        adjustmentsPy: prevBlock ? getPyFromPrev(prevBlock, "Lease Liabilities", "Adjustment of Modifications/Reassessment") : 0,
        accretionPy: prevBlock ? getPyFromPrev(prevBlock, "Lease Liabilities", "Accretion of Interest") : 0,
        paymentsPy: prevBlock ? Math.abs(getPyFromPrev(prevBlock, "Lease Liabilities", "Payments")) : 0,
        closingSumPy: prevBlock ? getPyFromPrev(prevBlock, "Lease Liabilities", "Closing Balance") : 0,
        closingPerSchedulePy: prevBlock
          ? getPyFromPrev(prevBlock, "Lease Liabilities", "Closing Balance as per all computations")
          : 0,
        tbAtRepPy: prevBlock ? getPyFromPrev(prevBlock, "Lease Liabilities", "Closing Balance as per TB") : 0,
      });

      const maturityResult = computeLeaseMaturityAnalysis(
        rep,
        aData,
        mData,
        rData,
        accExpData,
      );
      setMaturity({
        currentYear: maturityResult.currentYear,
        previousYear: {
          year1: prevBlock ? getPyFromPrev(prevBlock, "Maturity Analysis", "Year 1") : 0,
          year2: prevBlock ? getPyFromPrev(prevBlock, "Maturity Analysis", "Year 2") : 0,
          year3: prevBlock ? getPyFromPrev(prevBlock, "Maturity Analysis", "Year 3") : 0,
          year4: prevBlock ? getPyFromPrev(prevBlock, "Maturity Analysis", "Year 4") : 0,
          year5: prevBlock ? getPyFromPrev(prevBlock, "Maturity Analysis", "Year 5") : 0,
          year6Onwards: prevBlock ? getPyFromPrev(prevBlock, "Maturity Analysis", "Year 6 onwards") : 0,
          unmaturedFinanceCharges: prevBlock
            ? getPyFromPrev(prevBlock, "Maturity Analysis", "Less: unmatured finance charges")
            : 0,
          closingBalance: prevBlock ? getPyFromPrev(prevBlock, "Maturity Analysis", "Closing Balance") : 0,
          nonCurrent: prevBlock ? getPyFromPrev(prevBlock, "Maturity Analysis", "Non-current") : 0,
          current: prevBlock ? getPyFromPrev(prevBlock, "Maturity Analysis", "Current") : 0,
        },
      });

      const accExpDisclosureResult = computeLeaseAccountingExceptionDisclosure(rep, accExpData, accExpInpData);
      setAccExpDisclosure({
        currentYear: accExpDisclosureResult.currentYear,
        previousYear: {
          shortTermExpense: prevBlock
            ? getPyFromPrev(
                prevBlock,
                "Lease Accounting Exception",
                "Expense relating to short-term leases accounted for applying paragraph 6",
              )
            : 0,
          lowValueExpense: prevBlock
            ? getPyFromPrev(
                prevBlock,
                "Lease Accounting Exception",
                "Expense relating to lease of low-value assets accounted for applying paragraph 6",
              )
            : 0,
        },
      });
      const accExpReconCurr = currBlock?.["Lease Accounting Exception"]?.["User reconciliation"];
      const selectedCurr = Array.isArray(accExpReconCurr?.selected_ledgers)
        ? accExpReconCurr.selected_ledgers.map((x: any) => String(x))
        : [];
      setAccExpReconSelectedLedgers(selectedCurr);
      const accExpReconPrev = prevBlock?.["Lease Accounting Exception"]?.["User reconciliation"];
      setAccExpReconSelectedTotalPrev(asNumber(accExpReconPrev?.selected_total));

      const accExpUrRows = accExpReconCurr?.rows;
      if (Array.isArray(accExpUrRows)) {
        setAccExpUserReconciliations(
          accExpUrRows.map((r: any) => ({
            reason: String(r?.reason || ""),
            amount: Number(r?.amount) || 0,
          })),
        );
      } else {
        setAccExpUserReconciliations([]);
      }
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : "Failed to compute disclosure.");
      setComputed(null);
      setMaturity(null);
      setAccExpDisclosure(null);
      setAccExpReconLedgerOptions([]);
      setAccExpReconSelectedLedgers([]);
      setAccExpReconSelectedTotalPrev(0);
      setAccExpUserReconciliations([]);
      setHasPrevYearData(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadRecon();
  }, [loadRecon]);

  const addUserReconciliation = () => {
    setUserReconciliations((prev) => [...prev, { reason: "", amount: 0 }]);
  };

  const updateUserReconciliation = (index: number, field: "reason" | "amount", value: string) => {
    setUserReconciliations((prev) =>
      prev.map((row, i) =>
        i === index ? { ...row, [field]: field === "amount" ? Number(value) || 0 : value } : row,
      ),
    );
  };

  const removeUserReconciliation = (index: number) => {
    setUserReconciliations((prev) => prev.filter((_, i) => i !== index));
  };

  const addAccExpUserReconciliation = () => {
    setAccExpUserReconciliations((prev) => [...prev, { reason: "", amount: 0 }]);
  };

  const updateAccExpUserReconciliation = (index: number, field: "reason" | "amount", value: string) => {
    setAccExpUserReconciliations((prev) =>
      prev.map((row, i) =>
        i === index ? { ...row, [field]: field === "amount" ? Number(value) || 0 : value } : row,
      ),
    );
  };

  const removeAccExpUserReconciliation = (index: number) => {
    setAccExpUserReconciliations((prev) => prev.filter((_, i) => i !== index));
  };

  const saveRecon = async () => {
    setSaveStatus("saving");
    try {
      if (!computed) throw new Error("Compute first, then save.");
      if (!maturity) throw new Error("Compute Maturity Analysis first.");
      if (!accExpDisclosure) throw new Error("Compute Lease Accounting Exception table first.");
      if (!repDateKey) throw new Error("Reporting date key missing.");

      const fyYear = getFY_YEAR();
      if (!fyYear) throw new Error("FY_YEAR is not configured.");
      const userTotal = userReconciliations.reduce((s, r) => s + (Number(r.amount) || 0), 0);
      const accExpUserTotal = accExpUserReconciliations.reduce((s, r) => s + (Number(r.amount) || 0), 0);
      const accExpSelectedTotalCySave = accExpReconLedgerOptions
        .filter((x) => accExpReconSelectedLedgers.includes(x.ledger_name))
        .reduce((s, x) => s + asNumber(x.amount), 0);
      const accExpBaseCySave =
        (accExpDisclosure.currentYear.shortTermExpense || 0) + (accExpDisclosure.currentYear.lowValueExpense || 0);
      const accExpGrandTotalCySave = accExpBaseCySave - accExpSelectedTotalCySave;
      const accExpGrandTotalPySave =
        (accExpDisclosure.previousYear.shortTermExpense || 0) +
        (accExpDisclosure.previousYear.lowValueExpense || 0) -
        accExpReconSelectedTotalPrev;

      const c = computed;
      const m = maturity;
      const ae = accExpDisclosure;
      const diffScheduleCy = c.closingSumCy - c.closingPerScheduleCy;
      const diffSchedulePy = c.closingSumPy - c.closingPerSchedulePy;
      const diffTbCy = c.closingPerScheduleCy - c.tbAtRepCy;
      const diffTbPy = c.closingPerSchedulePy - c.tbAtRepPy;
      const netDiffCy = diffTbCy - userTotal;

      const dateBlock = {
        reportingDateLabel: repLabel,
        "Lease Liabilities": {
          "Opening Balance": { "Current Year": c.cyOpeningTb, "Previous Year": c.pyOpeningTb },
          "Additions": { "Current Year": c.additionsCy, "Previous Year": c.additionsPy },
          "Adjustment of Modifications/Reassessment": { "Current Year": c.adjustmentsCy, "Previous Year": c.adjustmentsPy },
          "Accretion of Interest": { "Current Year": c.accretionCy, "Previous Year": c.accretionPy },
          "Payments": { "Current Year": -c.paymentsCy, "Previous Year": -c.paymentsPy },
          "Closing Balance": { "Current Year": c.closingSumCy, "Previous Year": c.closingSumPy },
          "Closing Balance as per all computations": {
            "Current Year": c.closingPerScheduleCy,
            "Previous Year": c.closingPerSchedulePy,
          },
          "Difference (all computations - sum)": { "Current Year": diffScheduleCy, "Previous Year": diffSchedulePy },
          "Closing Balance as per TB": { "Current Year": c.tbAtRepCy, "Previous Year": c.tbAtRepPy },
          "Difference (schedule - TB)": { "Current Year": diffTbCy, "Previous Year": diffTbPy },
          "Net Difference": { "Current Year": netDiffCy },
          "User reconciliation": {
            rows: userReconciliations,
            userReconciliationTotal: userTotal,
          },
        },
        "Maturity Analysis": {
          "Year 1": { "Current Year": m.currentYear.year1, "Previous Year": m.previousYear.year1 },
          "Year 2": { "Current Year": m.currentYear.year2, "Previous Year": m.previousYear.year2 },
          "Year 3": { "Current Year": m.currentYear.year3, "Previous Year": m.previousYear.year3 },
          "Year 4": { "Current Year": m.currentYear.year4, "Previous Year": m.previousYear.year4 },
          "Year 5": { "Current Year": m.currentYear.year5, "Previous Year": m.previousYear.year5 },
          "Year 6 onwards": { "Current Year": m.currentYear.year6Onwards, "Previous Year": m.previousYear.year6Onwards },
          "Less: unmatured finance charges": {
            "Current Year": m.currentYear.unmaturedFinanceCharges,
            "Previous Year": m.previousYear.unmaturedFinanceCharges,
          },
          "Closing Balance": { "Current Year": m.currentYear.closingBalance, "Previous Year": m.previousYear.closingBalance },
          "Non-current": { "Current Year": m.currentYear.nonCurrent, "Previous Year": m.previousYear.nonCurrent },
          "Current": { "Current Year": m.currentYear.current, "Previous Year": m.previousYear.current },
        },
        "Lease Accounting Exception": {
          "Expense relating to short-term leases accounted for applying paragraph 6": {
            "Current Year": ae.currentYear.shortTermExpense,
            "Previous Year": ae.previousYear.shortTermExpense,
          },
          "Expense relating to lease of low-value assets accounted for applying paragraph 6": {
            "Current Year": ae.currentYear.lowValueExpense,
            "Previous Year": ae.previousYear.lowValueExpense,
          },
          "User reconciliation": {
            selected_ledgers: accExpReconSelectedLedgers,
            selected_total: accExpSelectedTotalCySave,
            rows: accExpUserReconciliations,
            userReconciliationTotal: accExpUserTotal,
          },
          "Grand Total": {
            "Current Year": accExpGrandTotalCySave,
            "Previous Year": accExpGrandTotalPySave,
          },
        },
      };

      // Merge with existing file to preserve other reporting dates.
      let root: Record<string, unknown> = {};
      try {
        const existing = await (window as any).sharePointAPI?.downloadClientJson?.(RECON_FILE);
        if (existing?.success && existing?.data && typeof existing.data === "object") {
          root = existing.data;
        }
      } catch {
        // ignore if file doesn't exist yet
      }

      const merged: Record<string, unknown> = {
        ...root,
        [repDateKey]: {
          ...dateBlock,
          lastUpdated: new Date().toISOString(),
        },
      };

      const jsonString = JSON.stringify(merged, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));
      const result = await (window as any).sharePointAPI?.uploadFile?.({
        fileContent: base64Content,
        fileName: RECON_FILE,
        referenceValue: "Lease Disclosure Reconciliation",
        folderName: "juggernaut",
        fyYear,
        sectionList: ["Execution_LEASE_DisclosureRecon"],
      });
      if (!result?.success) throw new Error(result?.error || "Upload failed");
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus("idle"), 2500);
    } catch (e) {
      console.error(e);
      setSaveStatus("error");
      setTimeout(() => setSaveStatus("idle"), 3000);
    }
  };

  const c = computed;
  const diffScheduleCy = c ? c.closingSumCy - c.closingPerScheduleCy : 0;
  const diffSchedulePy = c ? c.closingSumPy - c.closingPerSchedulePy : 0;
  const diffTbCy = c ? c.closingPerScheduleCy - c.tbAtRepCy : 0;
  const diffTbPy = c ? c.closingPerSchedulePy - c.tbAtRepPy : 0;
  const userTotal = userReconciliations.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const netDiffCy = diffTbCy - userTotal;
  const accExpSelectedTotalCy = accExpReconLedgerOptions
    .filter((x) => accExpReconSelectedLedgers.includes(x.ledger_name))
    .reduce((s, x) => s + asNumber(x.amount), 0);
  const accExpBaseCy =
    (accExpDisclosure?.currentYear.shortTermExpense || 0) + (accExpDisclosure?.currentYear.lowValueExpense || 0);
  const accExpBasePy =
    (accExpDisclosure?.previousYear.shortTermExpense || 0) + (accExpDisclosure?.previousYear.lowValueExpense || 0);
  const accExpUserTotal = accExpUserReconciliations.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const accExpDiffTbCy = accExpBaseCy - accExpSelectedTotalCy;
  const accExpNetDiffCy = accExpDiffTbCy - accExpUserTotal;
  const accExpGrandTotalCy = accExpBaseCy - accExpSelectedTotalCy;
  const accExpGrandTotalPy = accExpBasePy - accExpReconSelectedTotalPrev;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-white">Lease Disclosure</h2>
          <p className="text-gray-400">Lease liabilities roll-forward (Ind AS)</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={() => void runCompute()} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Loading…
              </>
            ) : (
              <>
                <Play className="h-4 w-4 mr-2" />
                Execution
              </>
            )}
          </Button>
          <Button onClick={() => void saveRecon()} size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" disabled={saveStatus === "saving"}>
            {saveStatus === "saving" ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving…
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save
              </>
            )}
          </Button>
          <Button variant="outline" onClick={() => setActiveSection?.("ll-substantive")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>
      </div>

      {loadError && (
        <div className="rounded border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-amber-100 text-sm">{loadError}</div>
      )}

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <CardTitle className="text-white">Lease Liabilities</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full min-w-[720px] border-collapse border border-white/10 text-sm">
            <thead>
              <tr className="bg-black/60">
                <th className="border border-white/10 px-3 py-2 text-left text-white font-semibold">Particulars</th>
                <th className="border border-white/10 px-3 py-2 text-right text-white font-semibold">Current Year</th>
                <th className="border border-white/10 px-3 py-2 text-right text-white font-semibold">Previous Year</th>
              </tr>
            </thead>
            <tbody className="text-white">
              <tr>
                <td className="border border-white/10 px-3 py-2 align-top">
                  Opening Balance
                </td>
                <td className="border border-white/10 px-3 py-2 text-right tabular-nums">{c ? fmt(c.cyOpeningTb) : "—"}</td>
                <td className="border border-white/10 px-3 py-2 text-right tabular-nums">{c ? fmtPrev(c.pyOpeningTb, hasPrevYearData) : "—"}</td>
              </tr>
              <tr>
                <td className="border border-white/10 px-3 py-2 align-top">
                  Additions
                </td>
                <td className="border border-white/10 px-3 py-2 text-right tabular-nums">{c ? fmt(c.additionsCy) : "—"}</td>
                <td className="border border-white/10 px-3 py-2 text-right tabular-nums">{c ? fmtPrev(c.additionsPy, hasPrevYearData) : "—"}</td>
              </tr>
              <tr>
                <td className="border border-white/10 px-3 py-2 align-top">
                  Adjustment of Modifications / Reassessment
                </td>
                <td className="border border-white/10 px-3 py-2 text-right tabular-nums">{c ? fmt(c.adjustmentsCy) : "—"}</td>
                <td className="border border-white/10 px-3 py-2 text-right tabular-nums">{c ? fmtPrev(c.adjustmentsPy, hasPrevYearData) : "—"}</td>
              </tr>
              <tr>
                <td className="border border-white/10 px-3 py-2 align-top">
                  Accretion of Interest
                </td>
                <td className="border border-white/10 px-3 py-2 text-right tabular-nums">{c ? fmt(c.accretionCy) : "—"}</td>
                <td className="border border-white/10 px-3 py-2 text-right tabular-nums">{c ? fmtPrev(c.accretionPy, hasPrevYearData) : "—"}</td>
              </tr>
              <tr>
                <td className="border border-white/10 px-3 py-2 align-top">Payments</td>
                <td className="border border-white/10 px-3 py-2 text-right tabular-nums">{c ? fmt(-c.paymentsCy) : "—"}</td>
                <td className="border border-white/10 px-3 py-2 text-right tabular-nums">{c ? fmtPrev(-c.paymentsPy, hasPrevYearData) : "—"}</td>
              </tr>
              <tr className="font-semibold">
                <td className="border border-white/10 px-3 py-2">Closing Balance</td>
                <td className="border border-white/10 px-3 py-2 text-right tabular-nums">{c ? fmt(c.closingSumCy) : "—"}</td>
                <td className="border border-white/10 px-3 py-2 text-right tabular-nums">{c ? fmtPrev(c.closingSumPy, hasPrevYearData) : "—"}</td>
              </tr>
              <tr>
                <td className="border border-white/10 px-3 py-2 align-top">
                  Closing Balance as per all computations
                </td>
                <td className="border border-white/10 px-3 py-2 text-right tabular-nums">{c ? fmt(c.closingPerScheduleCy) : "—"}</td>
                <td className="border border-white/10 px-3 py-2 text-right tabular-nums">{c ? fmtPrev(c.closingPerSchedulePy, hasPrevYearData) : "—"}</td>
              </tr>
              <tr>
                <td className="border border-white/10 px-3 py-2">Difference</td>
                <td className="border border-white/10 px-3 py-2 text-right tabular-nums">{c ? fmt(diffScheduleCy) : "—"}</td>
                <td className="border border-white/10 px-3 py-2 text-right tabular-nums">{c ? fmtPrev(diffSchedulePy, hasPrevYearData) : "—"}</td>
              </tr>
              <tr>
                <td className="border border-white/10 px-3 py-2 align-top">Closing Balance as per TB</td>
                <td className="border border-white/10 px-3 py-2 text-right tabular-nums">{c ? fmt(c.tbAtRepCy) : "—"}</td>
                <td className="border border-white/10 px-3 py-2 text-right tabular-nums">{c ? fmtPrev(c.tbAtRepPy, hasPrevYearData) : "—"}</td>
              </tr>
              <tr>
                <td className="border border-white/10 px-3 py-2">Difference</td>
                <td className="border border-white/10 px-3 py-2 text-right tabular-nums">{c ? fmt(diffTbCy) : "—"}</td>
                <td className="border border-white/10 px-3 py-2 text-right tabular-nums">{c ? fmtPrev(diffTbPy, hasPrevYearData) : "—"}</td>
              </tr>
            </tbody>
          </table>

          <div className="mt-6 space-y-3 border-t border-white/10 pt-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h3 className="text-white font-semibold">User reconciliation</h3>
              <Button type="button" size="sm" variant="outline" className="border-white/20 text-white" onClick={addUserReconciliation}>
                <Plus className="h-4 w-4 mr-2" />
                Add Row
              </Button>
            </div>
            <div className="overflow-x-auto rounded border border-white/10">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-black/60">
                    <th className="border border-white/10 px-3 py-2 text-left text-white">Particulars</th>
                    <th className="border border-white/10 px-3 py-2 text-right text-white">Amount (Current Year)</th>
                  </tr>
                </thead>
                <tbody>
                  {userReconciliations.map((row, index) => (
                    <tr key={`ur-${index}`} className="hover:bg-white/5">
                      <td className="border border-white/10 px-3 py-2">
                        <Input
                          value={row.reason}
                          onChange={(e) => updateUserReconciliation(index, "reason", e.target.value)}
                          className="border-white/10 bg-black/40 text-white"
                          placeholder="User reconciliation"
                        />
                      </td>
                      <td className="border border-white/10 px-3 py-2">
                        <div className="flex items-center justify-end gap-2">
                          <Input
                            type="number"
                            value={row.amount || ""}
                            onChange={(e) => updateUserReconciliation(index, "amount", e.target.value)}
                            className="border-white/10 bg-black/40 text-white text-right max-w-[200px]"
                            placeholder="Amount"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeUserReconciliation(index)}
                            className="text-red-400 hover:text-red-300 shrink-0"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-white/5 font-medium">
                    <td className="border border-white/10 px-3 py-2 text-white">Net Difference (TB difference − user reconciliation total)</td>
                    <td className="border border-white/10 px-3 py-2 text-right tabular-nums text-white">{c ? fmt(netDiffCy) : "—"}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </CardContent>
      </Card>


      <Card className="mt-6 border-white/10 bg-black/40">
        <CardHeader>
            <CardTitle className="text-white">Maturity Analysis</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full min-w-[720px] border-collapse border border-white/10 text-sm">
            <thead>
              <tr className="bg-black/60">
                <th className="border border-white/10 px-3 py-2 text-left text-white font-semibold">Particulars</th>
                <th className="border border-white/10 px-3 py-2 text-right text-white font-semibold">Current Year</th>
                <th className="border border-white/10 px-3 py-2 text-right text-white font-semibold">Previous Year</th>
              </tr>
            </thead>
            <tbody className="text-white">
              <tr>
                <td className="border border-white/10 px-3 py-2 align-top">Year 1</td>
                <td className="border border-white/10 px-3 py-2 text-right tabular-nums">{maturity ? fmt(maturity.currentYear.year1) : "—"}</td>
                <td className="border border-white/10 px-3 py-2 text-right tabular-nums">{maturity ? fmtPrev(maturity.previousYear.year1, hasPrevYearData) : "—"}</td>
              </tr>
              <tr>
                <td className="border border-white/10 px-3 py-2 align-top">Year 2</td>
                <td className="border border-white/10 px-3 py-2 text-right tabular-nums">{maturity ? fmt(maturity.currentYear.year2) : "—"}</td>
                <td className="border border-white/10 px-3 py-2 text-right tabular-nums">{maturity ? fmtPrev(maturity.previousYear.year2, hasPrevYearData) : "—"}</td>
              </tr>
              <tr>
                <td className="border border-white/10 px-3 py-2 align-top">Year 3</td>
                <td className="border border-white/10 px-3 py-2 text-right tabular-nums">{maturity ? fmt(maturity.currentYear.year3) : "—"}</td>
                <td className="border border-white/10 px-3 py-2 text-right tabular-nums">{maturity ? fmtPrev(maturity.previousYear.year3, hasPrevYearData) : "—"}</td>
              </tr>
              <tr>
                <td className="border border-white/10 px-3 py-2 align-top">Year 4</td>
                <td className="border border-white/10 px-3 py-2 text-right tabular-nums">{maturity ? fmt(maturity.currentYear.year4) : "—"}</td>
                <td className="border border-white/10 px-3 py-2 text-right tabular-nums">{maturity ? fmtPrev(maturity.previousYear.year4, hasPrevYearData) : "—"}</td>
              </tr>
              <tr>
                <td className="border border-white/10 px-3 py-2 align-top">Year 5</td>
                <td className="border border-white/10 px-3 py-2 text-right tabular-nums">{maturity ? fmt(maturity.currentYear.year5) : "—"}</td>
                <td className="border border-white/10 px-3 py-2 text-right tabular-nums">{maturity ? fmtPrev(maturity.previousYear.year5, hasPrevYearData) : "—"}</td>
              </tr>
              <tr>
                <td className="border border-white/10 px-3 py-2 align-top">Year 6 onwards</td>
                <td className="border border-white/10 px-3 py-2 text-right tabular-nums">{maturity ? fmt(maturity.currentYear.year6Onwards) : "—"}</td>
                <td className="border border-white/10 px-3 py-2 text-right tabular-nums">{maturity ? fmtPrev(maturity.previousYear.year6Onwards, hasPrevYearData) : "—"}</td>
              </tr>
              <tr>
                <td className="border border-white/10 px-3 py-2 align-top">Less: unmatured finance charges</td>
                <td className="border border-white/10 px-3 py-2 text-right tabular-nums">{maturity ? fmt(maturity.currentYear.unmaturedFinanceCharges) : "—"}</td>
                <td className="border border-white/10 px-3 py-2 text-right tabular-nums">{maturity ? fmtPrev(maturity.previousYear.unmaturedFinanceCharges, hasPrevYearData) : "—"}</td>
              </tr>
              <tr className="font-semibold">
                <td className="border border-white/10 px-3 py-2">Closing Balance</td>
                <td className="border border-white/10 px-3 py-2 text-right tabular-nums">{maturity ? fmt(maturity.currentYear.closingBalance) : "—"}</td>
                <td className="border border-white/10 px-3 py-2 text-right tabular-nums">{maturity ? fmtPrev(maturity.previousYear.closingBalance, hasPrevYearData) : "—"}</td>
              </tr>
              <tr>
                <td className="border border-white/10 px-3 py-2 align-top pl-6">Non-current</td>
                <td className="border border-white/10 px-3 py-2 text-right tabular-nums">{maturity ? fmt(maturity.currentYear.nonCurrent) : "—"}</td>
                <td className="border border-white/10 px-3 py-2 text-right tabular-nums">{maturity ? fmtPrev(maturity.previousYear.nonCurrent, hasPrevYearData) : "—"}</td>
              </tr>
              <tr>
                <td className="border border-white/10 px-3 py-2 align-top pl-6">Current</td>
                <td className="border border-white/10 px-3 py-2 text-right tabular-nums">{maturity ? fmt(maturity.currentYear.current) : "—"}</td>
                <td className="border border-white/10 px-3 py-2 text-right tabular-nums">{maturity ? fmtPrev(maturity.previousYear.current, hasPrevYearData) : "—"}</td>
              </tr>
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card className="mt-6 border-white/10 bg-black/40">
        <CardHeader>
          <CardTitle className="text-white">Lease Accounting Exception</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full min-w-[720px] border-collapse border border-white/10 text-sm">
            <thead>
              <tr className="bg-black/60">
                <th className="border border-white/10 px-3 py-2 text-left text-white font-semibold">Particulars</th>
                <th className="border border-white/10 px-3 py-2 text-right text-white font-semibold">Current Year</th>
                <th className="border border-white/10 px-3 py-2 text-right text-white font-semibold">Previous Year</th>
              </tr>
            </thead>
            <tbody className="text-white">
              <tr>
                <td className="border border-white/10 px-3 py-2 align-top">
                  Expense relating to short-term leases accounted for applying paragraph 6
                </td>
                <td className="border border-white/10 px-3 py-2 text-right tabular-nums">
                  {accExpDisclosure ? fmt(accExpDisclosure.currentYear.shortTermExpense) : "—"}
                </td>
                <td className="border border-white/10 px-3 py-2 text-right tabular-nums">
                  {accExpDisclosure ? fmtPrev(accExpDisclosure.previousYear.shortTermExpense, hasPrevYearData) : "—"}
                </td>
              </tr>
              <tr>
                <td className="border border-white/10 px-3 py-2 align-top">
                  Expense relating to lease of low-value assets accounted for applying paragraph 6
                </td>
                <td className="border border-white/10 px-3 py-2 text-right tabular-nums">
                  {accExpDisclosure ? fmt(accExpDisclosure.currentYear.lowValueExpense) : "—"}
                </td>
                <td className="border border-white/10 px-3 py-2 text-right tabular-nums">
                  {accExpDisclosure ? fmtPrev(accExpDisclosure.previousYear.lowValueExpense, hasPrevYearData) : "—"}
                </td>
              </tr>
              <tr>
                <td className="border border-white/10 px-3 py-2 align-top">
                  User reconciliation (FS Subline 20036 ledgers)
                  <div className="mt-2">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button type="button" variant="outline" size="sm" className="border-white/20 text-white">
                          {accExpReconSelectedLedgers.length
                            ? `${accExpReconSelectedLedgers.length} selected`
                            : "Select ledgers"}
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="max-h-72 w-[360px] overflow-auto border-white/10 bg-black/90 text-white">
                        {accExpReconLedgerOptions.map((opt) => (
                          <DropdownMenuCheckboxItem
                            key={opt.ledger_name}
                            checked={accExpReconSelectedLedgers.includes(opt.ledger_name)}
                            onCheckedChange={(checked) => {
                              setAccExpReconSelectedLedgers((prev) =>
                                checked
                                  ? [...new Set([...prev, opt.ledger_name])]
                                  : prev.filter((x) => x !== opt.ledger_name),
                              );
                            }}
                          >
                            {opt.ledger_name} ({fmt(opt.amount)})
                          </DropdownMenuCheckboxItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  {accExpReconSelectedLedgers.length > 0 && (
                    <ul className="mt-3 max-h-40 overflow-y-auto space-y-1 rounded border border-white/10 bg-black/30 px-2 py-2 text-xs text-gray-300">
                      {accExpReconSelectedLedgers.map((name) => {
                        const opt = accExpReconLedgerOptions.find((o) => o.ledger_name === name);
                        return (
                          <li key={name} className="flex justify-between gap-3 tabular-nums">
                            <span className="min-w-0 break-words text-left">{name}</span>
                            <span className="shrink-0 text-white">{opt ? fmt(opt.amount) : "—"}</span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </td>
                <td className="border border-white/10 px-3 py-2 text-right tabular-nums">{fmt(-accExpSelectedTotalCy)}</td>
                <td className="border border-white/10 px-3 py-2 text-right tabular-nums">
                  {hasPrevYearData ? fmt(-accExpReconSelectedTotalPrev) : "-"}
                </td>
              </tr>
              <tr className="font-semibold">
                <td className="border border-white/10 px-3 py-2 align-top">Grand Total</td>
                <td className="border border-white/10 px-3 py-2 text-right tabular-nums">{fmt(accExpGrandTotalCy)}</td>
                <td className="border border-white/10 px-3 py-2 text-right tabular-nums">
                  {hasPrevYearData ? fmt(accExpGrandTotalPy) : "-"}
                </td>
              </tr>
            </tbody>
          </table>

          <div className="mt-6 space-y-3 border-t border-white/10 pt-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h3 className="text-white font-semibold">User reconciliation</h3>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="border-white/20 text-white"
                onClick={addAccExpUserReconciliation}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Row
              </Button>
            </div>
            <div className="overflow-x-auto rounded border border-white/10">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-black/60">
                    <th className="border border-white/10 px-3 py-2 text-left text-white">Particulars</th>
                    <th className="border border-white/10 px-3 py-2 text-right text-white">Amount (Current Year)</th>
                  </tr>
                </thead>
                <tbody className="text-white">
                  <tr className="bg-white/5">
                    <td className="border border-white/10 px-3 py-2">Total (short-term + low-value expense per computation)</td>
                    <td className="border border-white/10 px-3 py-2 text-right tabular-nums">
                      {accExpDisclosure ? fmt(accExpBaseCy) : "—"}
                    </td>
                  </tr>
                  <tr className="bg-white/5">
                    <td className="border border-white/10 px-3 py-2">Total (FS Subline 20036 ledgers selected)</td>
                    <td className="border border-white/10 px-3 py-2 text-right tabular-nums">{fmt(accExpSelectedTotalCy)}</td>
                  </tr>
                  <tr className="bg-white/5">
                    <td className="border border-white/10 px-3 py-2">Difference</td>
                    <td className="border border-white/10 px-3 py-2 text-right tabular-nums">{accExpDisclosure ? fmt(accExpDiffTbCy) : "—"}</td>
                  </tr>
                  {accExpUserReconciliations.map((row, index) => (
                    <tr key={`ae-ur-${index}`} className="hover:bg-white/5">
                      <td className="border border-white/10 px-3 py-2">
                        <Input
                          value={row.reason}
                          onChange={(e) => updateAccExpUserReconciliation(index, "reason", e.target.value)}
                          className="border-white/10 bg-black/40 text-white"
                          placeholder="User reconciliation"
                        />
                      </td>
                      <td className="border border-white/10 px-3 py-2">
                        <div className="flex items-center justify-end gap-2">
                          <Input
                            type="number"
                            value={row.amount || ""}
                            onChange={(e) => updateAccExpUserReconciliation(index, "amount", e.target.value)}
                            className="border-white/10 bg-black/40 text-white text-right max-w-[200px]"
                            placeholder="Amount"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeAccExpUserReconciliation(index)}
                            className="text-red-400 hover:text-red-300 shrink-0"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-white/5 font-medium">
                    <td className="border border-white/10 px-3 py-2 text-white">User reconciliation total</td>
                    <td className="border border-white/10 px-3 py-2 text-right tabular-nums text-white">{fmt(accExpUserTotal)}</td>
                  </tr>
                  <tr className="bg-white/5 font-medium">
                    <td className="border border-white/10 px-3 py-2 text-white">
                      Net Difference (difference − user reconciliation total)
                    </td>
                    <td className="border border-white/10 px-3 py-2 text-right tabular-nums text-white">
                      {accExpDisclosure ? fmt(accExpNetDiffCy) : "—"}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
